import { useDataStore } from "@store/user_org";
import { useMemo } from "react";

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function formatLastActive(ts: any): string {
  if (!ts) return "Jamais";
  try {
    const date = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 3) return "En ligne";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return "Hier";
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  } catch {
    return "Inconnu";
  }
}

export default function TeamActivityWidget() {
  const orgMembers = useDataStore((s) => s.orgMembers);

  const activeMembers = useMemo(() => {
    return orgMembers
      .filter((m) => m.status !== "inactive")
      .sort((a, b) => {
        const aTime = a.activity?.last_active_time?.seconds || 0;
        const bTime = b.activity?.last_active_time?.seconds || 0;
        return bTime - aTime;
      })
      .slice(0, 10);
  }, [orgMembers]);

  return (
    <div className="flex flex-col bg-white border rounded-xl p-4 gap-2 shadow-lg">
      <div className="font-semibold text-black mb-1">Activité de l'équipe</div>
      {activeMembers.length === 0 && (
        <div className="text-h3 text-neutral-400 py-2">Aucun membre</div>
      )}
      <div className="flex flex-col divide-y">
        {activeMembers.map((member) => {
          const lastActive = formatLastActive(member.activity?.last_active_time);
          const isOnline = lastActive === "En ligne";
          return (
            <div key={member.id} className="flex flex-row items-center py-2 px-1 gap-3">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-h4 font-medium flex-shrink-0 relative"
                style={{ backgroundColor: member.customization?.color_tag || "#6366F1" }}
              >
                {member.customization?.photo_url ? (
                  <img src={member.customization.photo_url} className="w-8 h-8 rounded-full object-cover" alt="" />
                ) : (
                  getInitials(member.infos?.display_name || member.infos?.email || "?")
                )}
                {isOnline && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-500 border-2 border-white" />
                )}
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <div className="text-h2 font-medium text-black truncate">{member.infos?.display_name || member.infos?.email}</div>
                <div className="text-h4 text-neutral-400">{member.role || "Membre"}</div>
              </div>
              <span className={`text-h4 flex-shrink-0 ${isOnline ? "text-green-500 font-medium" : "text-neutral-400"}`}>
                {lastActive}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
